// Posting a completed forecast's report summary into its OWN conversation, exactly once.
//
// The backend returns a structured `report.forecast_summary`; chatboard surfaces it as a
// chat message carrying a "Full Report" link (the same action as the resolution pie). The
// fresh-submit path posts it (submitForecast.ts, via pushSayerMsg), but the resume/reattach
// path finalizes the run WITHOUT posting — so a forecast that completed while the tab was
// closed, or while the user was looking at a different forecast, never showed its summary on
// click-back. Deep forecasts, being slow, hit that path routinely (hence "worked on quick but
// not deep"). And because a run can finalize through more than one path (fresh submit, then a
// reload-resume, or a user-triggered reattach on an already-complete run), an unconditional
// post can double-write.
//
// This module owns the two pure, path-agnostic pieces of the invariant "post the summary once,
// from any completion path": extracting the summary text, and detecting whether a run's
// conversation already carries it. The actual append stays in each command with the mechanism
// that path already uses (submit hands pushSayerMsg a target naming its run, resolved against the
// live store; resume dispatches entryAppended directly, mirroring its writeTerminalChat) — so the
// dedup guard is shared while each path keeps its own, separately-load-bearing routing.

import { cleanForecastSummary } from "../report/blocks.js";
import { extractMultiway } from "./multiway.js";
import { runAuthoredNoForecast } from "./unauthoredRun.js";

// The structured report's forecast_summary as a chat-ready string, or null when the backend
// produced no summary. Looks in BOTH locations the report can live — top-level `report` and the
// nested `serverContext.report` — the SAME field the report modal's summary paragraph reads, and
// runs it through the SAME cleanForecastSummary sanitizer the modal + exports use, so the leading
// question-restatement / probability-recap sentence is peeled off and the chat text agrees with
// the report. Trims first, so a whitespace-only summary posts nothing (a benign edge the backend
// never emits); cleanForecastSummary never blanks a real summary, so a cleaned result stays truthy.
export function reportSummaryText(source) {
  const rpt = source?.report || source?.serverContext?.report || null;
  const s = rpt?.forecast_summary;
  if (typeof s !== "string" || !s.trim()) return null;
  const cleaned = cleanForecastSummary(s);
  return cleaned && cleaned.trim() ? cleaned : null;
}

/**
 * The summary the report's ASSESSMENT block shows, raw (uncleaned), or null.
 *
 * THE one definition, shared by the report modal and the chat post so the two cannot
 * disagree — they did: chat was built from the `explanation` narrative while the report
 * rendered this, so a multiway forecast showed completely different prose in each place.
 *
 * `report.forecast_summary` wins whenever it is present, whatever the question type. Only
 * when it is absent does a DISTRIBUTION fall back to the flash-generated
 * `multi_report.forecast_summary`, a THIRD-PERSON analyst-voice summary. So this is a
 * PRECEDENCE, not a per-type switch: it reads as "binary uses the first, multiway the
 * second" exactly while binary results carry a `report` and multiway ones do not, and it
 * keeps doing the right thing if that stops being true. Mirrors the modal.
 *
 * Deliberately NOT the `explanation` / `distribution_reasoning.aggregation` text, which is
 * the forecaster agent's raw first-person reasoning ("I weight the betting market consensus…",
 * naming its own tools). Reports keep that voice out, so the chat must too. To make the
 * multiway summary richer, enrich `generate_multi_report` server-side; do not substitute the
 * agent voice here.
 *
 * The multi_report fallback is gated on the source really being a distribution
 * (`extractMultiway`), matching the modal: a binary row must never borrow it.
 *
 * @param {object} source completed-forecast or report-source shape
 * @returns {string|null}
 */
export function assessmentSummaryOf(source) {
  // A run that authored no report has no assessment to show. Its `multi_report` was
  // generated OVER the placeholder distribution, so it reads as a confident analysis of a
  // forecast that was never made ("maximum uncertainty", narrating a uniform). Returning null
  // here covers the report modal and the chat post together, which is the whole point of this
  // being the one shared definition — and the predicate reads the raw wire shape too, because
  // the branch/submit/resume flows call `assessmentText` with a completion object that never
  // passed through `reportPayloadToSource`.
  if (runAuthoredNoForecast(source)) return null;
  const r = source?.report || source?.serverContext?.report || null;
  const mr =
    source?.multi_report ||
    source?.contract?.multi_report ||
    source?.serverContext?.multi_report ||
    null;
  const isDistribution = !!extractMultiway(source);
  return r?.forecast_summary || (isDistribution ? mr?.forecast_summary : null) || null;
}

// The assessment a completed forecast posts into its own conversation, or null — the SAME
// text the report's ASSESSMENT block renders, through the same sanitizer, so the chat post
// is the top of the report rather than a separately-derived summary of it.
export function assessmentText(source) {
  const raw = assessmentSummaryOf(source);
  if (typeof raw !== "string" || !raw.trim()) return null;
  const cleaned = cleanForecastSummary(raw);
  return cleaned && cleaned.trim() ? cleaned : null;
}

// The client conversation id a run's chat lives under: its explicit `conversationId`, or the run
// id itself as the fallback (the same resolution submit/resume already use — see chatTarget.ts's
// resolveChatTarget and writeTerminalChat's wtConvId).
export function summaryConvId(state, runId) {
  return state?.runsById?.[runId]?.conversationId ?? runId;
}

// True once the run's own conversation already carries a Full-Report summary message (any entry
// with `metadata.reportLink`). The dedup guard: a run can finalize through several paths and a
// user-facing reattach can re-run on an already-complete run, so callers gate their post on this
// to keep the summary EXACTLY once no matter how many of those fire. metadata rides the message
// round-trip (entryToMessageRow keeps non-reserved keys) and is restored on hydrate, so the guard
// also holds across a reload.
export function hasForecastSummaryMessage(state, runId) {
  if (!runId) return false;
  const convId = summaryConvId(state, runId);
  const entries = state?.conversationsById?.[convId]?.entries ?? [];
  // Matched on THIS run, not merely on the conversation. Branches share their root's
  // conversation, so a bare "any entry carries reportLink" test went true the moment the root
  // posted its own summary — and every branch after it was then skipped, leaving the user no
  // summary line and no way to open that branch's report from the thread. Still exactly one
  // per run: the guard's original job (a run can finalize through several paths, and a
  // reattach can re-run an already-complete one) is unchanged, just scoped correctly.
  return entries.some((e) => e?.metadata?.reportLink && e?.runId === runId);
}
